/**
 * Capacity Outlook (Brett, 2026-09-21) — the time axis capacity was
 * missing, derived from data the board already keeps.
 *
 * Every assignment is flat hours/week on a project; every project has
 * a go-live (end_date). So "who frees up when" falls out with nothing
 * new to enter: a person's committed hours in a given week = the sum
 * of their assignment hours on projects whose window covers that week.
 *
 * Rules (kept deliberately simple — no phases, no ramps):
 *   - Complete/archived projects contribute nothing.
 *   - A project with no end date holds its hours every week (flagged).
 *   - A project PAST its go-live that isn't complete still holds its
 *     hours every week, flagged "past due" — pressure to close it out,
 *     not a silent lie.
 *   - A project with no start date is treated as already started.
 *   - Only roster members with include_in_capacity count; "available"
 *     is their project_capacity_hrs (weekly hrs × (1 − reserve)).
 *
 * ROLE GATE (Brett, 2026-09-21 — "Ravindra reads 333% but Core Transfer
 * hasn't reached tech spec"): an assignment's hours are ACTIVE only
 * once the project's status reaches the point where that role's work
 * begins; before that they're QUEUED (booked, visible, not counted).
 *   - PM and BA: active from Approved through Hypercare.
 *   - Functional, Technical, Developer (and DBA / Infrastructure / ERP):
 *     active from Technical spec through Testing / go-live; queued in
 *     Approved and Functional spec; off during Hypercare.
 *   - Requested, On hold, Complete, Declined: nothing counts, any role.
 */

export type AssignmentState = "active" | "queued" | "off";

const STATUS_ORDER = [
  "requested", "approved", "functional_spec", "technical_spec",
  "ready_for_development", "in_progress", "testing_go_live", "hypercare",
];
const EARLY_ROLES = new Set(["pm", "ba"]);

/** Where a role's hours start counting, and what the lookup shows. */
export function assignmentState(roleKey: string | null | undefined, status: string | null | undefined): AssignmentState {
  const s = (status ?? "").toLowerCase();
  const idx = STATUS_ORDER.indexOf(s);
  if (idx < 0 || s === "requested") return "off"; // complete / postponed / declined / unknown / requested
  const early = EARLY_ROLES.has((roleKey ?? "").toLowerCase());
  if (early) return "active"; // approved … hypercare
  if (s === "hypercare") return "off";
  return idx >= STATUS_ORDER.indexOf("technical_spec") ? "active" : "queued";
}

export function stateReason(state: AssignmentState, roleKey: string | null | undefined, status: string | null | undefined): string {
  const s = (status ?? "").toLowerCase();
  const early = EARLY_ROLES.has((roleKey ?? "").toLowerCase());
  if (state === "active") return "";
  if (s === "requested") return "not approved yet";
  if (s === "postponed") return "on hold";
  if (s === "complete") return "complete";
  if (s === "declined") return "declined";
  if (s === "hypercare") return early ? "" : "hypercare — delivery hours released";
  if (state === "queued") return "starts at Technical spec";
  return "not counted";
}
import type { Project } from "@/types/project";
import type { TeamMember } from "@/types/roster";

export interface OutlookAssignment {
  project_id: string;
  person_name: string;
  role_key?: string | null;
  hours_per_week?: number | null;
}

export interface OutlookWeek {
  /** ISO date of the week's Monday. */
  start: string;
  label: string;
}

export interface OutlookProjectHold {
  id: string;
  name: string;
  hours: number;
  startDate: string | null;
  endDate: string | null;
  pastDue: boolean;
  undated: boolean;
  /** Per grid week: does this project hold its hours that week? */
  weeks: boolean[];
  /** Role gate: counted, booked-but-not-started, or not counted. */
  state: AssignmentState;
  reason: string;
  status: string | null;
  roleKey: string | null;
}

export interface OutlookCell {
  /** ACTIVE hours this week — what the percentage is built from. */
  committed: number;
  /** Booked hours whose role hasn't started yet (project not at the
   *  status where this role's work begins). Shown, not counted. */
  queued: number;
  /** Portion of `committed` held by past-due or undated projects. */
  flagged: number;
}

export interface OutlookPerson {
  name: string;
  team: string;
  available: number;
  cells: OutlookCell[];
  holds: OutlookProjectHold[];
}

export interface OutlookTeam {
  team: string;
  available: number;
  cells: OutlookCell[];
  people: OutlookPerson[];
}

export interface OutlookRelease {
  date: string | null;
  label: string;
  pastDue: boolean;
  projects: Array<{ id: string; name: string; hours: number }>;
  people: Array<{ name: string; hours: number }>;
  totalHours: number;
}

export interface Outlook {
  weeks: OutlookWeek[];
  teams: OutlookTeam[];
  releases: OutlookRelease[];
  /** Hours held by projects past their go-live — the cleanup number. */
  pastDueHours: number;
  undatedHours: number;
}

function toLocal(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Monday on or before `d` (local). */
export function weekStart(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = out.getDay(); // 0 = Sunday
  out.setDate(out.getDate() - ((dow + 6) % 7));
  return out;
}

const shortLabel = (d: Date) =>
  d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

export function buildOutlook(input: {
  people: TeamMember[];
  assignments: OutlookAssignment[];
  projects: Project[];
  today?: Date;
  weeks?: number;
  /** Capacity scope (2026-10-06): only projects in these board bands count.
   *  Omitted = every project (unit-test default). */
  countedTiers?: Set<string>;
}): Outlook {
  const today = input.today ?? new Date();
  const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const nWeeks = input.weeks ?? 13;
  const first = weekStart(todayMid);
  const weeks: OutlookWeek[] = Array.from({ length: nWeeks }, (_, i) => {
    const s = new Date(first);
    s.setDate(first.getDate() + i * 7);
    return { start: isoDate(s), label: shortLabel(s) };
  });
  const weekBounds = weeks.map((w) => {
    const s = toLocal(w.start)!;
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    return { s, e };
  });

  const byId = new Map<string, Project>();
  for (const p of input.projects) byId.set(p.id, p);

  const people = input.people
    .filter((m) => m.include_in_capacity)
    .sort(
      (a, b) =>
        (a.team ?? "zzz").localeCompare(b.team ?? "zzz") || a.name.localeCompare(b.name),
    );
  const byPerson = new Map<string, OutlookAssignment[]>();
  for (const a of input.assignments) {
    if (!(a.hours_per_week && a.hours_per_week > 0)) continue;
    const p = byId.get(a.project_id);
    if (!p || p.archived_at) continue;
    const list = byPerson.get(a.person_name) ?? [];
    list.push(a);
    byPerson.set(a.person_name, list);
  }

  // Per-project window facts, computed once.
  const holdFor = (a: OutlookAssignment): OutlookProjectHold => {
    const p = byId.get(a.project_id)!;
    const start = toLocal(p.start_date);
    const end = toLocal(p.end_date);
    const pastDue = !!end && end < todayMid;
    const undated = !end;
    const weeksCovered = weekBounds.map(({ s, e }) => {
      if (undated || pastDue) return true; // holds every week
      if (end! < s) return false;
      if (start && start > e) return false;
      return true;
    });
    // Status reasons win (requested, on hold…); the scope reason only
    // applies to a booking that would otherwise count.
    const byStatus = assignmentState(a.role_key, p.status);
    const inScope = !input.countedTiers || input.countedTiers.has((p.priority_tier ?? "").toLowerCase());
    const state: AssignmentState = byStatus === "off" || inScope ? byStatus : "off";
    const reason = state === byStatus ? stateReason(state, a.role_key, p.status) : "not in a band that counts toward capacity (Admin → Capacity scope)";
    return {
      id: p.id,
      name: p.name,
      hours: a.hours_per_week ?? 0,
      startDate: start ? isoDate(start) : null,
      endDate: end ? isoDate(end) : null,
      pastDue,
      undated,
      weeks: weeksCovered,
      state,
      reason,
      status: p.status ?? null,
      roleKey: a.role_key ?? null,
    };
  };

  const personRows: OutlookPerson[] = people.map((m) => {
    const list = byPerson.get(m.name) ?? [];
    const holds = list.map(holdFor).sort((x, y) => (x.endDate ?? "9999").localeCompare(y.endDate ?? "9999"));
    const cells = weeks.map((_, wi) => {
      let committed = 0;
      let queued = 0;
      let flagged = 0;
      for (const h of holds) {
        if (!h.weeks[wi] || h.state === "off") continue;
        if (h.state === "queued") { queued += h.hours; continue; }
        committed += h.hours;
        if (h.pastDue || h.undated) flagged += h.hours;
      }
      return { committed, queued, flagged };
    });
    return { name: m.name, team: (m.team ?? "").trim() || "No team", available: m.project_capacity_hrs, cells, holds };
  });

  const teamMap = new Map<string, OutlookPerson[]>();
  for (const r of personRows) teamMap.set(r.team, [...(teamMap.get(r.team) ?? []), r]);
  const teams: OutlookTeam[] = Array.from(teamMap.entries())
    .sort((a, b) => (a[0] === "No team" ? 1 : b[0] === "No team" ? -1 : a[0].localeCompare(b[0])))
    .map(([team, rows]) => ({
      team,
      available: rows.reduce((s, r) => s + r.available, 0),
      cells: weeks.map((_, wi) => ({
        committed: rows.reduce((s, r) => s + r.cells[wi].committed, 0),
        queued: rows.reduce((s, r) => s + r.cells[wi].queued, 0),
        flagged: rows.reduce((s, r) => s + r.cells[wi].flagged, 0),
      })),
      people: rows,
    }));

  // Releases: by go-live, which projects close and who frees up.
  const relMap = new Map<string, { projects: Map<string, { id: string; name: string; hours: number }>; people: Map<string, number>; pastDue: boolean; date: string | null }>();
  let pastDueHours = 0;
  let undatedHours = 0;
  for (const r of personRows) {
    for (const h of r.holds) {
      if (h.state === "off") continue;
      if (h.undated) { undatedHours += h.hours; continue; }
      if (h.pastDue) pastDueHours += h.hours;
      const key = h.endDate!;
      const g = relMap.get(key) ?? { projects: new Map(), people: new Map(), pastDue: h.pastDue, date: key };
      const pj = g.projects.get(h.id) ?? { id: h.id, name: h.name, hours: 0 };
      pj.hours += h.hours;
      g.projects.set(h.id, pj);
      g.people.set(r.name, (g.people.get(r.name) ?? 0) + h.hours);
      relMap.set(key, g);
    }
  }
  const releases: OutlookRelease[] = Array.from(relMap.values())
    .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""))
    .map((g) => {
      const d = toLocal(g.date)!;
      const projects = Array.from(g.projects.values()).sort((a, b) => b.hours - a.hours);
      const people = Array.from(g.people.entries()).map(([name, hours]) => ({ name, hours })).sort((a, b) => b.hours - a.hours);
      return {
        date: g.date,
        label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        pastDue: g.pastDue,
        projects,
        people,
        totalHours: projects.reduce((s, p) => s + p.hours, 0),
      };
    });

  return { weeks, teams, releases, pastDueHours, undatedHours };
}

/** Load tone for a cell: share of available that's committed. */
export function loadTone(committed: number, available: number): "idle" | "ok" | "full" | "over" {
  if (committed <= 0) return "idle";
  if (available <= 0) return "over";
  const r = committed / available;
  if (r <= 0.85) return "ok";
  if (r <= 1.0) return "full";
  return "over";
}
